/** California county coverage levels for UI disclosure (mirrors Python registry). */

export type Coverage_Level = "full" | "partial" | "unavailable";

export type Layer_Coverage = {
  Level: Coverage_Level;
  Notes?: string;
  Portal_Url?: string | null;
  Blocker?: string | null;
};

export type County_Coverage = {
  County: string;
  Assessor: Layer_Coverage;
  Tax: Layer_Coverage;
  Recorder: Layer_Coverage;
  Deep_Zoning: Layer_Coverage;
  Layers: {
    assessor: Coverage_Level;
    tax: Coverage_Level;
    recorder: Coverage_Level;
    deep_zoning: Coverage_Level;
  };
  Can_Purchase: boolean;
  Disclosure: string;
};

const Coverage_Disclosure =
  "Report depth varies by county. Missing tax or recorder data is disclosed, not invented.";

const Tax_Full_Counties = new Set(["CONTRA COSTA"]);
/** Partial tax plugins: Orange, Alameda, + Megabyte MPTS counties. */
const Tax_Partial_Counties = new Set([
  "ORANGE",
  "ALAMEDA",
  "AMADOR",
  "BUTTE",
  "CALAVERAS",
  "COLUSA",
  "DEL NORTE",
  "EL DORADO",
  "GLENN",
  "HUMBOLDT",
  "IMPERIAL",
  "KINGS",
  "LAKE",
  "MADERA",
  "MARIPOSA",
  "MERCED",
  "MODOC",
  "MONO",
  "MONTEREY",
  "NAPA",
  "NEVADA",
  "PLACER",
  "PLUMAS",
  "SAN BENITO",
  "SAN JOAQUIN",
  "SHASTA",
  "SISKIYOU",
  "SONOMA",
  "STANISLAUS",
  "TEHAMA",
  "TULARE",
  "TUOLUMNE",
  "YOLO",
  "YUBA",
  "LASSEN",
  "SIERRA",
  "ALPINE",
  "SAN LUIS OBISPO",
  "FRESNO",
  "RIVERSIDE",
  "SANTA BARBARA",
  "INYO",
  "SAN MATEO",
  "SACRAMENTO",
  "KERN",
  "SAN FRANCISCO",
  "LOS ANGELES",
  "SANTA CLARA",
  "SANTA CRUZ",
]);
const Recorder_Full_Counties = new Set(["CONTRA COSTA"]);
const Recorder_Partial_Counties = new Set([
  "ORANGE",
  "STANISLAUS",
  "IMPERIAL",
  "MODOC",
  "AMADOR",
  "SISKIYOU",
  "MERCED",
]);
const Assessor_Full_Counties = new Set(["CONTRA COSTA"]);
const Assessor_Value_Counties = new Set([
  "ALAMEDA",
  "EL DORADO",
  "LOS ANGELES",
  "ORANGE",
  "RIVERSIDE",
  "SAN BERNARDINO",
  "SAN DIEGO",
  "SAN FRANCISCO",
  "SAN JOAQUIN",
  "SANTA BARBARA",
  "SONOMA",
  "VENTURA",
]);
const Assessor_Partial_Enrich = new Set([
  "BUTTE",
  "SONOMA",
  "NAPA",
  "SACRAMENTO",
  "LOS ANGELES",
  "ALAMEDA",
  "EL DORADO",
  "FRESNO",
  "ORANGE",
  "SAN DIEGO",
  "SAN FRANCISCO",
  "SANTA CLARA",
  "SAN MATEO",
  "RIVERSIDE",
  "SAN BERNARDINO",
  "KERN",
  "VENTURA",
  "SAN JOAQUIN",
  "SANTA BARBARA",
]);
const Deep_Zoning_Partial = new Set([
  "CONTRA COSTA",
  "LOS ANGELES",
  "ORANGE",
  "RIVERSIDE",
  "SAN BERNARDINO",
  "VENTURA",
  "IMPERIAL",
  "SAN DIEGO",
  "SAN FRANCISCO",
  "SANTA CLARA",
  "ALAMEDA",
  "SACRAMENTO",
  "FRESNO",
  "KERN",
  "STANISLAUS",
  "PLACER",
  "SAN JOAQUIN",
  "TULARE",
  "SONOMA",
  "SOLANO",
  "MONTEREY",
  "SAN MATEO",
  "MERCED",
]);

const Tax_Portal_Urls: Record<string, string> = {
  "CONTRA COSTA": "https://taxcolp.cccttc.us/",
  ORANGE: "https://taxbill.octreasurer.gov/",
  ALAMEDA: "https://propertytax.alamedacountyca.gov/search",
  SACRAMENTO: "https://county-taxes.net/sacramento/property-tax",
  "SAN MATEO": "https://county-taxes.net/ca-sanmateo/services",
  KERN: "https://kcttc.co.kern.ca.us/Payment/mainsearch.aspx",
  "SANTA CLARA": "https://santaclaracounty.telleronline.net/",
  "LOS ANGELES": "https://vcheck.ttc.lacounty.gov/",
  "SAN DIEGO": "https://www.sdtreastax.com/",
  "SAN FRANCISCO": "https://county-taxes.net/ca-sanfrancisco/property-tax",
  "SANTA CRUZ": "https://ttc.co.santa-cruz.ca.us/Taxbills/Home",
  VENTURA: "https://taxpayment.venturacounty.gov/webtaxonline/index.html",
};

function County_Name_Normalize(County: string | null | undefined): string {
  return (County || "").trim().toUpperCase();
}

function Layer_Build(
  Level: Coverage_Level,
  Notes: string,
  Extra?: Partial<Layer_Coverage>,
): Layer_Coverage {
  return { Level, Notes, ...Extra };
}

export function County_Coverage_Get(County: string | null | undefined): County_Coverage {
  const Norm = County_Name_Normalize(County) || "UNKNOWN";

  const Assessor = Assessor_Full_Counties.has(Norm)
    ? Layer_Build("full", "CCMAP ArcGIS assessor parcels")
    : Assessor_Partial_Enrich.has(Norm)
      ? Layer_Build(
          "partial",
          Assessor_Value_Counties.has(Norm)
            ? Norm === "SAN FRANCISCO"
              ? "CA LightBox + DataSF secured roll land/imp"
              : Norm === "ORANGE"
                ? "CA LightBox + SCAG total Valuation"
                : Norm === "RIVERSIDE"
                  ? "CA LightBox + CREST land/imp (partial) + SCAG gross"
                  : "CA LightBox + county ArcGIS land/imp values"
            : "CA LightBox + county ArcGIS enrichment",
        )
      : Layer_Build("partial", "CA DWR LightBox statewide assessor parcels");

  const Tax = Tax_Full_Counties.has(Norm)
    ? Layer_Build("full", "County tax portal scraper", {
        Portal_Url: Tax_Portal_Urls[Norm],
      })
    : Tax_Partial_Counties.has(Norm)
      ? Layer_Build("partial", "County tax portal scraper (partial / layout-fragile)", {
          Portal_Url: Tax_Portal_Urls[Norm],
        })
      : Layer_Build(
          "unavailable",
          Norm === "SAN DIEGO"
            ? "Secured roll seasonal (mid/late Sept–Oct); BillSearch UI exists"
            : Norm === "VENTURA"
              ? "WebTaxOnline WAF intermittent in headless — Unlocker / fingerprint later"
              : "Tax portal adapter blocked or not shipped yet",
          {
            Blocker:
              Norm === "SAN DIEGO"
                ? "seasonal_secured_roll"
                : Norm === "VENTURA"
                  ? "waf_reject"
                  : "adapter_pending",
            Portal_Url: Tax_Portal_Urls[Norm] ?? null,
          },
        );

  const Recorder = Recorder_Full_Counties.has(Norm)
    ? Layer_Build("full", "County recorder index scraper")
    : Recorder_Partial_Counties.has(Norm)
      ? Layer_Build("partial", "County recorder APN index scraper")
      : Layer_Build(
          "unavailable",
          "No public APN-keyed recorder index (name/doc-only or landing). Paid index optional later.",
          { Blocker: "no_public_apn_index" },
        );

  const Deep_Zoning = Deep_Zoning_Partial.has(Norm)
    ? Layer_Build("partial", "Municipal zoning plugin where city matches")
    : Layer_Build("unavailable", "Statewide zoning layer only", {
        Blocker: "statewide_only",
      });

  return {
    County: Norm,
    Assessor,
    Tax,
    Recorder,
    Deep_Zoning,
    Layers: {
      assessor: Assessor.Level,
      tax: Tax.Level,
      recorder: Recorder.Level,
      deep_zoning: Deep_Zoning.Level,
    },
    Can_Purchase: true,
    Disclosure: Coverage_Disclosure,
  };
}

export function Coverage_Level_Label(Level: Coverage_Level): string {
  if (Level === "full") return "Full";
  if (Level === "partial") return "Partial";
  return "Unavailable";
}
